import _, { Dictionary } from "lodash";

import { SLAPSHOT_HOME_URL } from "constants/urls";
import { Price } from "models/Payment";
import { ShotCompact } from "models/Shot";
import { UserDetail } from "models/User";

import { AUTH_USER, SERVICES, SHOT_SOURCES } from "../constants/constants";

export const getUser = () => {
  const user = localStorage.getItem(AUTH_USER);
  if (user) {
    return JSON.parse(user);
  }
  return null;
};

// An absent flag counts as verified; the backend enforces this regardless.
export const isEmailVerified = (): boolean =>
  getUser()?.email_verified !== false;

export const getRedirectPath = (location: { search: string }): string => {
  const urlParams = new URLSearchParams(location.search);
  const nextParam = urlParams.get("next");

  if (nextParam) {
    try {
      const decodedPath = decodeURIComponent(nextParam);
      if (decodedPath.startsWith("/")) {
        return decodedPath;
      }
    } catch {
      console.warn("Failed to decode next parameter:", nextParam);
    }
  }

  return SLAPSHOT_HOME_URL;
};

export const getInitials = () => {
  const user = getUser();
  if (!user) {
    return "";
  }
  return user.name.split(" ").map((v: string) => v[0]);
};

export const getFirstName = () => {
  const user = getUser();
  if (!user) {
    return "";
  }
  return user.name.split(" ")[0];
};

export const getStatusString = (status: string) => {
  switch (status) {
    case "IN_PROGRESS":
      return "In Progress";
    case "COMPLETED":
      return "Completed";
    case "FAILED":
      return "Failed";
    // no default
  }
  return "";
};

export const getStatusColor = (status: string) => {
  switch (status) {
    case "IN_PROGRESS":
      return "orange";
    case "COMPLETED":
      return "green";
    case "FAILED":
      return "red";
    // no default
  }
  return "";
};

export const imageURLToDataURL = (
  imageURL: string,
  callback: (dataURL: string) => void,
): void => {
  fetch(imageURL)
    .then((response) => response.blob())
    .then((blob) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === "string") {
          callback(reader.result);
        }
      };
      reader.readAsDataURL(blob);
    })
    .catch((error) => console.error("Error fetching image:", error));
};

export const formatDate = (dateString: string | Date) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year:
      date.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
  });
};

export const formatMonthYear = (dateString: string) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateString;
  }
};

export const formatDateToYMD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const formatTimestamp = (
  timestamp,
  format: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
    year: "numeric",
  },
) => {
  return new Intl.DateTimeFormat("en-US", format).format(
    new Date(timestamp * 1000),
  );
};

export const getDaysFromToday = (dateString: string): number => {
  const givenDate = new Date(dateString);
  const today = new Date();

  givenDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const differenceInTime = givenDate.getTime() - today.getTime();
  return Math.max(0, Math.ceil(differenceInTime / (1000 * 60 * 60 * 24)));
};

export const mergeDict = (
  obj1: Dictionary<ShotCompact[]>,
  obj2: Dictionary<ShotCompact[]>,
): Dictionary<ShotCompact[]> => {
  const resultDict: Dictionary<ShotCompact[]> = { ...obj1 };

  Object.keys(obj2).forEach((key) => {
    if (Object.hasOwn(resultDict, key)) {
      resultDict[key] = [...obj2[key], ...resultDict[key]];
    } else {
      resultDict[key] = obj2[key];
    }
  });
  return resultDict;
};

export const getRelativeDate = (created: Date, text = "Created") => {
  const currentTime = new Date();
  const diff = currentTime.getTime() - new Date(created).getTime();

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30); // Rough estimation, not precise
  const years = Math.floor(days / 365); // Rough estimation, not precise

  let creationText = "";
  if (seconds < 60) {
    creationText = `${text} a few seconds ago`;
  } else if (minutes < 60) {
    creationText = `${text} ${minutes} minute${minutes !== 1 ? "s" : ""} ago`;
  } else if (hours < 24) {
    creationText = `${text} ${hours} hour${hours !== 1 ? "s" : ""} ago`;
  } else if (days < 7) {
    creationText = `${text} ${days} day${days !== 1 ? "s" : ""} ago`;
  } else if (weeks < 4 || months === 0) {
    creationText = `${text} ${weeks} week${weeks !== 1 ? "s" : ""} ago`;
  } else if (months < 12) {
    creationText = `${text} ${months} month${months !== 1 ? "s" : ""} ago`;
  } else {
    creationText = `${text} ${years} year${years !== 1 ? "s" : ""} ago`;
  }

  return creationText;
};

export const hexToRGB = (hexColor: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexColor);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 0, g: 0, b: 0 };
};

export const getCurrencySymbol = (currencyCode: string) => {
  return new Intl.NumberFormat("en", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    ?.formatToParts(1)
    ?.find((part) => part.type === "currency")?.value;
};

export const convertUnitAmountToAmount = (unitAmount: number, round = 2) => {
  return parseFloat((unitAmount / 100).toFixed(round));
};

export const formatPrice = (price: Price, round = 2) => {
  const amount = convertUnitAmountToAmount(price.unit_amount_decimal, round);
  return `${getCurrencySymbol(price.currency)}${amount.toLocaleString(
    undefined,
    {
      minimumFractionDigits: 0,
      maximumFractionDigits: round,
    },
  )}`;
};

export const formatSize = (
  size,
  unit: "B" | "KB" | "MB" | "GB" | "TB" | "PB" = "B",
) => {
  if (size === 0) return `0 ${unit}`;

  const units = ["B", "KB", "MB", "GB", "TB", "PB"];

  const initialUnitIndex = units.indexOf(unit);
  if (initialUnitIndex === -1) throw new Error("Invalid unit");

  const sizeInBytes = size * 1024 ** initialUnitIndex;

  const displayUnitIndex = Math.floor(Math.log(sizeInBytes) / Math.log(1024));
  const convertedSize = (sizeInBytes / 1024 ** displayUnitIndex).toFixed(2);

  return `${parseFloat(convertedSize)} ${units[displayUnitIndex]}`;
};

export const arrayToObjectById = (arr) => {
  return arr.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});
};

export const getTextInitials = (text: string) => {
  return text.split(" ").map((v: string) => v[0].toUpperCase());
};

export const getUserNameText = (user: UserDetail) => {
  const currentUser = getUser();
  if (!user) {
    return "You";
  }
  let text = user.name;
  if (user.last_name) {
    text += ` ${user.last_name}`;
  }
  if (currentUser.email === user.email) {
    text += ` (you)`;
  }
  return text;
};

export const getSingularOrPlural = (count: number, singular: string) => {
  return count === 1 ? singular : `${singular}s`;
};

export const getServiceDisplayName = (value) => {
  switch (value) {
    case SERVICES.RETIME:
      return "Generative Retime";
    case SERVICES.TRACKING:
      return "Camera Tracking";
    case SERVICES.SMART_VECTORS:
    case "Smart Vectors":
      return "Motion Vectors";
    default:
      return value;
  }
};

export const toSnakeCaseKeys = (
  obj: Record<string, unknown>,
): Record<string, unknown> => {
  return _.mapKeys(obj, (_value, key) => _.snakeCase(key));
};

export const isShotBillable = (shot: {
  source: string;
  is_demo_shot: boolean;
}): boolean => {
  return !(
    [SHOT_SOURCES.HS].includes(shot.source as SHOT_SOURCES) || shot.is_demo_shot
  );
};
